import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, ShieldCheck, MapPin, SlidersHorizontal, RefreshCw, Sparkles, Heart, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { optionLabel } from "@/lib/profileLabels";
import FilterSheet from "@/components/FilterSheet";
import MatchModal from "@/components/MatchModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FILTERS_KEY = "nvl.discovery.filters";
const SORT_KEY = "nvl.discovery.sort";

const SORT_MODES = ["recommended", "most_compatible", "nearby", "new_here", "recently_active"];

function loadFilters() {
  try {
    const v = window.sessionStorage.getItem(FILTERS_KEY);
    return v ? JSON.parse(v) : { interests_match: "any" };
  } catch {
    return { interests_match: "any" };
  }
}
function saveFilters(f) {
  try { window.sessionStorage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch {}
}
function loadSort() {
  try { return window.sessionStorage.getItem(SORT_KEY) || "recommended"; } catch { return "recommended"; }
}
function saveSort(s) { try { window.sessionStorage.setItem(SORT_KEY, s); } catch {} }

function serializeFilters(filters) {
  const p = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      if (v.length === 0) return;
      p[k] = v.join(",");
    } else if (typeof v === "boolean") {
      if (v) p[k] = "true";
    } else {
      p[k] = v;
    }
  });
  return p;
}

function countActive(filters) {
  return Object.entries(filters).reduce((acc, [k, v]) => {
    if (k === "interests_match") return acc;
    if (v === undefined || v === null || v === "" || v === false) return acc;
    if (Array.isArray(v) && v.length === 0) return acc;
    return acc + 1;
  }, 0);
}

function photoUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

function DiscoverCard({ card, onOpen, onLike, onPass, busy }) {
  const { lang, t } = useI18n();
  const img = photoUrl(card.main_photo_url);
  const compat = card.compatibility || {};
  return (
    <div
      className="nvl-card overflow-hidden text-left w-full transition-transform hover:-translate-y-0.5 flex flex-col"
      data-testid={`discover-card-${card.user_id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="text-left w-full"
        data-testid={`discover-card-open-${card.user_id}`}
      >
        <div
          className="relative aspect-[4/5] w-full flex items-end p-5"
          style={{
            backgroundImage: img ? `url(${img})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
            background: img ? undefined : "linear-gradient(135deg, hsl(6 43% 46% / 0.25), hsl(223 66% 10% / 0.95))",
            color: "hsl(var(--nvl-bg))",
          }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: img
                ? "linear-gradient(180deg, transparent 40%, rgba(10,17,40,0.78))"
                : undefined,
            }}
          />
          <div className="relative w-full flex justify-between items-end">
            <div>
              {card.verified ? (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest mb-2"
                  style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
                  data-testid={`card-verified-${card.user_id}`}
                >
                  <ShieldCheck size={11} /> {t("app.discovery.verified")}
                </span>
              ) : null}
              <h3 className="font-display text-2xl md:text-3xl leading-tight">
                {card.display_name}
                {card.age ? <span className="opacity-80">, {card.age}</span> : null}
              </h3>
              {card.city ? (
                <p className="mt-1 text-xs flex items-center gap-1.5 opacity-90">
                  <MapPin size={11} /> {card.city}
                  {typeof card.distance_km === "number" ? ` · ${Math.round(card.distance_km)} ${t("app.discovery.km")}` : ""}
                </p>
              ) : null}
            </div>
            {compat?.confidence === "low" ? null : (
              <div className="text-right">
                <div
                  className="font-display text-2xl leading-none"
                  data-testid={`card-compat-${card.user_id}`}
                >
                  {compat.score}%
                </div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">
                  {t("app.discovery.compatibleShort")}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-4 md:p-5">
          {compat?.confidence === "low" ? (
            <p className="text-xs italic" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.discovery.confidenceLearning")}
            </p>
          ) : compat?.confidence === "medium" ? (
            <p className="text-[11px] italic mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.discovery.confidenceLimited")}
            </p>
          ) : null}

          {card.bio ? (
            <p className="text-sm line-clamp-3" style={{ color: "hsl(var(--nvl-ink))" }}>
              {card.bio}
            </p>
          ) : null}

          {card.interests?.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {card.interests.slice(0, 4).map((i) => (
                <span
                  key={i}
                  className="px-2.5 py-0.5 rounded-full text-[11px] border"
                  style={{ borderColor: "hsl(var(--nvl-border))" }}
                >
                  {optionLabel(lang, "interests", i)}
                </span>
              ))}
            </div>
          ) : null}

          {card.top_values?.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {card.top_values.slice(0, 3).map((v) => (
                <span
                  key={v}
                  className="px-2.5 py-0.5 rounded-full text-[11px]"
                  style={{ backgroundColor: "hsl(var(--muted))" }}
                >
                  {optionLabel(lang, "values", v)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </button>

      {/* Action row */}
      <div
        className="p-4 flex gap-3 border-t"
        style={{ borderColor: "hsl(var(--nvl-border))" }}
      >
        <button
          type="button"
          onClick={onPass}
          disabled={busy}
          aria-label={t("app.discovery.pass")}
          data-testid={`discover-pass-${card.user_id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm border transition-colors hover:bg-[hsl(var(--muted))] disabled:opacity-50"
          style={{ borderColor: "hsl(var(--nvl-border))" }}
        >
          <X size={16} />
          {t("app.discovery.pass")}
        </button>
        <button
          type="button"
          onClick={onLike}
          disabled={busy}
          aria-label={t("app.discovery.like")}
          data-testid={`discover-like-${card.user_id}`}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm text-[hsl(var(--nvl-bg))] transition-colors disabled:opacity-50"
          style={{ backgroundColor: "hsl(var(--nvl-accent))" }}
        >
          <Heart size={16} />
          {t("app.discovery.like")}
        </button>
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="nvl-card overflow-hidden animate-pulse">
      <div className="aspect-[4/5] w-full" style={{ backgroundColor: "hsl(var(--muted))" }} />
      <div className="p-5 space-y-3">
        <div className="h-3 w-3/4 rounded" style={{ backgroundColor: "hsl(var(--muted))" }} />
        <div className="h-3 w-1/2 rounded" style={{ backgroundColor: "hsl(var(--muted))" }} />
        <div className="h-3 w-2/3 rounded" style={{ backgroundColor: "hsl(var(--muted))" }} />
      </div>
    </div>
  );
}

export default function Discover() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(loadFilters);
  const [sort, setSort] = useState(loadSort);
  const [showFilters, setShowFilters] = useState(false);
  const [options, setOptions] = useState(null);

  const [cards, setCards] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [busyUserId, setBusyUserId] = useState(null);
  const [matchPayload, setMatchPayload] = useState(null);
  const loadKey = useRef(0);

  // Load options once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/options");
        setOptions(data);
      } catch {
        // Not fatal — filter grid just stays empty
      }
    })();
  }, []);

  const load = useCallback(async (nextPage = 1, append = false) => {
    const my = ++loadKey.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError("");
    try {
      const params = { page: nextPage, page_size: 20, sort, ...serializeFilters(filters) };
      const { data } = await api.get("/discovery", { params });
      if (loadKey.current !== my) return;  // stale response
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setPage(nextPage);
      setCards((prev) => (append ? [...prev, ...(data.cards || [])] : data.cards || []));
    } catch (e) {
      if (loadKey.current === my) setError(extractError(e, t("app.discovery.loadingError")));
    } finally {
      if (loadKey.current === my) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort, t]);

  useEffect(() => { load(1, false); }, [load]);

  const applyFilters = (next) => {
    setFilters(next);
    saveFilters(next);
  };
  const changeSort = (s) => {
    setSort(s);
    saveSort(s);
  };

  const activeCount = countActive(filters);

  const openProfile = (userId) => navigate(`/app/discover/${userId}`);

  const removeCard = (userId) => {
    setCards((prev) => prev.filter((c) => c.user_id !== userId));
  };
  const restoreCard = (userId, card) => {
    setCards((prev) => {
      if (prev.some((c) => c.user_id === userId)) return prev;
      return [card, ...prev];
    });
  };

  const undoInteraction = async (userId, snapshot) => {
    try {
      await api.post("/interactions/undo", { target_user_id: userId });
      restoreCard(userId, snapshot);
      toast.success(t("app.discovery.undoToast"), {
        "data-testid": "toast-undone",
      });
    } catch (e) {
      toast.error(extractError(e, t("app.discovery.actionError")));
    }
  };

  const handleLike = async (card) => {
    if (busyUserId) return;
    setBusyUserId(card.user_id);
    const snapshot = { ...card };
    removeCard(card.user_id);
    try {
      const { data } = await api.post("/interactions/like", {
        target_user_id: card.user_id,
      });
      if (data?.match) {
        setMatchPayload(data.match);
      } else {
        toast(t("app.discovery.likeSent"), {
          action: {
            label: t("app.discovery.undoLike"),
            onClick: () => undoInteraction(card.user_id, snapshot),
          },
          duration: 5000,
          "data-testid": "toast-like",
        });
      }
    } catch (e) {
      restoreCard(card.user_id, snapshot);
      toast.error(extractError(e, t("app.discovery.actionError")));
    } finally {
      setBusyUserId(null);
    }
  };

  const handlePass = async (card) => {
    if (busyUserId) return;
    setBusyUserId(card.user_id);
    const snapshot = { ...card };
    removeCard(card.user_id);
    try {
      await api.post("/interactions/pass", { target_user_id: card.user_id });
      toast(t("app.discovery.passDone"), {
        action: {
          label: t("app.discovery.undoPass"),
          onClick: () => undoInteraction(card.user_id, snapshot),
        },
        duration: 5000,
        "data-testid": "toast-pass",
      });
    } catch (e) {
      restoreCard(card.user_id, snapshot);
      toast.error(extractError(e, t("app.discovery.actionError")));
    } finally {
      setBusyUserId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="page-discover">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
            <Compass size={12} className="inline mr-1 -mt-0.5" /> {t("app.discovery.title")}
          </p>
          <h1 className="mt-3 text-4xl md:text-5xl font-display">
            {t("app.discovery.title")}
          </h1>
          <p className="mt-2 text-sm max-w-xl" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.discovery.subtitle")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => changeSort(e.target.value)}
            className="nvl-input text-xs"
            data-testid="discover-sort-select"
          >
            {SORT_MODES.map((s) => (
              <option key={s} value={s}>{t(`app.discovery.sort.${s}`)}</option>
            ))}
          </select>

          <button
            onClick={() => setShowFilters(true)}
            className="nvl-btn nvl-btn-outline"
            data-testid="discover-filters-button"
          >
            <SlidersHorizontal size={14} className="mr-2" />
            {t("app.discovery.filtersButton")}
            {activeCount > 0 ? (
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-[10px]"
                style={{ backgroundColor: "hsl(var(--nvl-accent))", color: "hsl(var(--nvl-bg))" }}
                data-testid="discover-filters-count"
              >
                {activeCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="discover-skeleton">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : error ? (
          <div className="nvl-card p-8 text-center" data-testid="discover-error">
            <p className="text-sm" style={{ color: "hsl(var(--destructive))" }}>{error}</p>
            <button onClick={() => load(1, false)} className="mt-4 nvl-btn nvl-btn-outline" data-testid="discover-retry">
              <RefreshCw size={14} className="mr-2" /> {t("app.discovery.retry")}
            </button>
          </div>
        ) : cards.length === 0 ? (
          <div className="nvl-card p-10 text-center" data-testid="discover-empty">
            <Sparkles size={22} style={{ color: "hsl(var(--nvl-accent))" }} className="mx-auto" />
            <p className="mt-4 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.discovery.empty")}
            </p>
            <button
              onClick={() => applyFilters({ interests_match: "any" })}
              className="mt-4 nvl-btn nvl-btn-outline"
              data-testid="discover-broaden"
            >
              {t("app.discovery.broaden")}
            </button>
          </div>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="discover-grid">
              {cards.map((c) => (
                <DiscoverCard
                  key={c.user_id}
                  card={c}
                  onOpen={() => openProfile(c.user_id)}
                  onLike={() => handleLike(c)}
                  onPass={() => handlePass(c)}
                  busy={busyUserId === c.user_id}
                />
              ))}
            </div>
            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => load(page + 1, true)}
                  disabled={loadingMore}
                  className="nvl-btn nvl-btn-outline"
                  data-testid="discover-load-more"
                >
                  {loadingMore ? t("common.loading") : t("app.discovery.loadMore")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {options ? (
        <FilterSheet
          open={showFilters}
          onClose={() => setShowFilters(false)}
          initial={filters}
          options={options}
          onApply={applyFilters}
        />
      ) : null}

      <MatchModal
        open={!!matchPayload}
        match={matchPayload}
        onClose={() => setMatchPayload(null)}
        onKeepDiscovering={() => setMatchPayload(null)}
      />
    </div>
  );
}
